import React from "react";
import { noop } from "lodash";
import { AnimatePresence, motion, MotionProps } from "motion/react";
import { createPortal } from "react-dom";

import { Component } from "../../../../commons/types/component";
import cn from "../../libs/class-name";

export type ModalContainerPosition = "center" | "top" | "bottom" | "left" | "right" | "top-left" | "top-right";
export type ModalContainerAnimation = "slide-in-left" | "fade-in";
type AnimationVariant = Record<
  ModalContainerAnimation,
  MotionProps["initial"] | MotionProps["animate"] | MotionProps["exit"]
>;

const renderProps = (element: Props["children"], isOpen: boolean, onClose: () => void) =>
  typeof element === "function" ? element(isOpen, onClose) : element;

interface Props extends Component {
  children: React.ReactNode | ((isOpen: boolean, onClose: () => void) => React.ReactNode);
  rootNode?: HTMLElement;
  position?: ModalContainerPosition;
  hideBackground?: boolean;
  isOpen?: boolean;
  keyboardOpen?: boolean;
  onClose?: () => void;
  /**
   * fade-in is used for a common modals
   * slide-in-left is used for help modals which are sliding in from the right side of the screen
   */
  animation?: ModalContainerAnimation;
  animationDuration?: number;
}

const ModalContainer = ({
  classNames = [],
  children,
  rootNode,
  position = "center",
  hideBackground = false,
  isOpen = false,
  keyboardOpen = false,
  onClose = noop,
  animation = "fade-in",
  animationDuration = 0.3
}: Props) => {
  const nodeRef = React.useRef<HTMLDivElement>(null);

  const contentAnimationInitialAndExit: AnimationVariant = {
    ["slide-in-left"]: {
      x: "100%"
    },
    ["fade-in"]: {
      opacity: 0,
      scale: 0.8
    }
  };
  const contentAnimationAnimate: AnimationVariant = {
    ["slide-in-left"]: {
      x: 0
    },
    ["fade-in"]: {
      opacity: 1,
      scale: 1
    }
  };

  // AnimatePresence has to live inside the portal: `isValidElement` is false for portal objects,
  // so AnimatePresence would drop them and never run the exit animation.
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className={cn(
            "ModalContainer",
            [{ [`position-${position}`]: position, hideBackground, keyboardOpen }],
            classNames
          )}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ ease: "easeOut", duration: animationDuration }}
            ref={nodeRef}
            className="ModalContainer__background"
            onClick={onClose}
          />
          <motion.div
            initial={contentAnimationInitialAndExit[animation] as MotionProps["initial"]}
            animate={contentAnimationAnimate[animation] as MotionProps["animate"]}
            exit={contentAnimationInitialAndExit[animation] as MotionProps["exit"]}
            transition={{
              ease: "easeOut",
              duration: animationDuration
            }}
            className="ModalContainer__content"
          >
            {renderProps(children, isOpen, onClose)}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    rootNode ?? window.document.body
  );
};

export default ModalContainer;
